import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, throwIfError } from "../client";
import type { components } from "../generated/schema";
import type { PhotoCategoryId } from "@/types/identifiers";

export type PhotoCategory = Omit<components["schemas"]["PhotoCategoryResponse"], "id" | "parent_id" | "children"> & {
  id: PhotoCategoryId;
  parent_id: PhotoCategoryId | null;
  children: PhotoCategory[];
};

export const photoCategoryKeys = {
  all: ["photo-categories"] as const,
};

export function usePhotoCategories() {
  return useQuery({
    queryKey: photoCategoryKeys.all,
    queryFn: async () => {
      const result = await api.GET("/api/photo-categories");
      return throwIfError(result, "Failed to fetch photo categories") as PhotoCategory[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useReorderCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { id: PhotoCategoryId; parent_id: PhotoCategoryId | null; sort_order: number }[]) => {
      const result = await api.PUT("/api/photo-categories/reorder", {
        body: { items },
      });
      return throwIfError(result, "Failed to reorder categories");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: photoCategoryKeys.all });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
